import type { ReactNode } from 'react';

import { usePageTitle } from '@/shared/hooks/usePageTitle';

/**
 * R-050 PageHeader — and, for a record, the R-050 record header: the way back
 * above the title (where the reference's breadcrumb sits), a status or version
 * badge beside the title, the meta line under it, and the actions aligned with
 * the top of the block rather than floating in its middle.
 *
 * `eyebrow` is the record's reference line above its title, as the enquiry
 * Goldens draw it (GS-13/14/15): the number, its state and where it came from,
 * so the title can be what the record is about.
 *
 * Its title also names the browser tab, "<title> · Fidelen" (A11Y-04).
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  badge,
  back,
  eyebrow,
  tourId,
  tourState,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** A status or version badge beside the title. */
  badge?: ReactNode;
  /** The way back out of a record, above its title. */
  back?: ReactNode;
  /** The record's reference line above its title: number, state, origin. */
  eyebrow?: ReactNode;
  /** Guided-tour anchor, rendered as `data-tour` on the root (an `AnchorId`; CONTRACT §3). */
  tourId?: string;
  /** Authoritative record state for an outcome-bound coach's resume probe. */
  tourState?: string;
}) {
  usePageTitle(title);

  return (
    <header
      data-tour={tourId}
      data-guidance-state={tourId ? tourState : undefined}
      className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
    >
      <div className="min-w-0">
        {back ? <div className="-ms-2 mb-1">{back}</div> : null}
        {eyebrow ? (
          <div className="type-meta mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-muted">
            {eyebrow}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="type-page-title wrap-break-word text-ink-heading">{title}</h1>
          {badge}
        </div>
        {subtitle ? <div className="type-meta mt-1 text-ink-muted">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2 sm:shrink-0">{actions}</div> : null}
    </header>
  );
}
